import useLocalPaginator, {
  PageInfo,
  useLocalPaginationState,
} from "@dashboard/hooks/useLocalPaginator";
import { useEffect, useState } from "react";

export const FITMENT_PAGINATE_BY = 50;

export interface WsmFitmentPagingProps {
  rowNumber: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  onRowNumberChange: (rowNumber: number) => void;
  onNextPage: () => void;
  onPreviousPage: () => void;
}

/**
 * The paging state a values or applications table needs, in one place: the
 * variables to query with, and `bind(pageInfo)` for the props the table takes
 * once the page has come back.
 */
export const useWsmFitmentPagedList = () => {
  const [rowNumber, setRowNumber] = useState(FITMENT_PAGINATE_BY);
  const [paginationState, setPaginationState] = useLocalPaginationState(rowNumber);
  const paginate = useLocalPaginator(setPaginationState);
  const bind = (pageInfo: PageInfo | undefined | null): WsmFitmentPagingProps => {
    const {
      loadNextPage,
      loadPreviousPage,
      pageInfo: paged,
    } = paginate(pageInfo ?? undefined, paginationState);

    return {
      rowNumber,
      hasNextPage: paged?.hasNextPage ?? false,
      hasPreviousPage: paged?.hasPreviousPage ?? false,
      onRowNumberChange: value => {
        setRowNumber(value);
        setPaginationState({});
      },
      onNextPage: loadNextPage,
      onPreviousPage: loadPreviousPage,
    };
  };

  // A new filter starts from the first page: a cursor from the old one means nothing.
  const resetPage = () => setPaginationState({});

  return { variables: paginationState, bind, resetPage };
};

/**
 * A page that comes back empty while a cursor is set is stale: the last row of
 * the last page was deleted (or a filter emptied it), and the refetch kept the
 * cursor. Fall back to the first page rather than show "no values" over rows
 * that exist; the cursor of the page before is not known here. `rows` is
 * undefined while loading, which is not empty.
 */
export const useWsmFitmentEmptyPageFallback = (
  variables: { after?: string; before?: string },
  rows: unknown[] | undefined,
  resetPage: () => void,
) => {
  const stale = !!(variables.after || variables.before) && rows?.length === 0;

  useEffect(() => {
    if (stale) {
      resetPage();
    }
    // resetPage is a fresh closure each render; only the stale edge matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stale]);
};
